import type { PartialPropsOf, PropsOf, ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { Targeting } from "~/components/advertisement/ad-slots/types";
import { ContentActionBar } from "~/components/content/content-action-bar";
import { ContentAuthorLink } from "~/components/content/content-author-link";
import { ContentCoverImage } from "~/components/content/content-cover-image";
import { ContentCoverVideo } from "~/components/content/content-cover-video";
import { ContentHeadline } from "~/components/content/content-headline";
import { ContentItemMagazinesStyleArticleMoreOnThis } from "~/components/content/content-item-render/variants/magazines-style-article-more-on-this";
import { ContentLiveTag } from "~/components/content/content-live-tag";
import { ContentPrintHeadline } from "~/components/content/content-print-headline";
import { ContentPublishedDate } from "~/components/content/content-published-date";
import { ContentSections } from "~/components/content/content-sections";
import { useContentSponsor } from "~/components/content/content-sponsor-tag/hooks";
import { ContentSubHeadline } from "~/components/content/content-sub-headline";
import { ContentSummary } from "~/components/content/content-summary";
import { ContentTopicLink } from "~/components/content/content-topic-link";
import { SponsorTag } from "~/components/sponsor-tag";
import { VideoDurationOverlay } from "~/components/video/video-duration-overlay";
import type { contentItemRenderContent$key } from "~/queries/__generated__/contentItemRenderContent.graphql";
import type { CustomQueryParameters } from "~/types/gtm";

import { ContentItemCard } from "./variants/card";
import { ContentItemHomePrimary } from "./variants/home-primary";
import type { ContentItemHomeSecondaryVariantProps } from "./variants/home-secondary";
import { ContentItemHomeSecondary } from "./variants/home-secondary";
import {
  ContentItemMagazinesStyleCardSquare,
  ContentItemMagazinesStyleCardWide,
} from "./variants/magazines-style-card";
import { ContentItemStyleFashionCarousel } from "./variants/magazines-style-fashion-carousel";
import type { ContentItemMagazinesStyleLeadVariantProps } from "./variants/magazines-style-lead";
import { ContentItemMagazinesStyleLead } from "./variants/magazines-style-lead";
import { ContentItemMagazinesStyleSpotlight } from "./variants/magazines-style-spotlight";
import { ContentItemMorningStudio } from "./variants/morning-studio";
import { ContentItemOpinion } from "./variants/opinion";
import { ContentItemPostiesPrimary } from "./variants/posties-primary";
import { ContentItemPostiesSecondary } from "./variants/posties-secondary";
import { ContentItemPostiesSectionPrimary } from "./variants/posties-section-primary";
import { ContentItemPostiesSectionSecondary } from "./variants/posties-section-secondary";
import { ContentItemPrimary } from "./variants/primary";
import { ContentItemSearch } from "./variants/search";
import { ContentItemSecondary } from "./variants/secondary";

type Props = {
  advertisement?: {
    targeting: Targeting;
    zone: string;
  };
  className?: string;
  customQueryParameters?: CustomQueryParameters;
  reference: contentItemRenderContent$key;
  variant: Variant;
  withAuthorLink?: boolean;
  withTopicLink?: Omit<PropsOf<typeof ContentTopicLink>, "reference">;
} & (
  | { variant: Exclude<Variant, "magazines-style-lead"> }
  | { variant: Exclude<Variant, "home-secondary"> }
  | ({
      variant: "magazines-style-lead";
    } & ContentItemMagazinesStyleLeadVariantProps)
  | ({
      variant: "home-secondary";
    } & ContentItemHomeSecondaryVariantProps)
);

type Variant =
  | "primary"
  | "secondary"
  | "search"
  | "opinion"
  | "card"
  | "home-primary"
  | "home-secondary"
  | "magazines-style-fashion-carousel"
  | "magazines-style-spotlight"
  | "morning-studio"
  | "magazines-style-lead"
  | "magazines-style-card-wide"
  | "magazines-style-card-square"
  | "magazines-style-article-more-on-this"
  | "posties-primary"
  | "posties-secondary"
  | "posties-section-primary"
  | "posties-section-secondary";

export type DefaultComponentsProvider = {
  actionBar: (props?: PartialPropsOf<typeof ContentActionBar>) => ReactNode;
  authorLink?: (props?: PartialPropsOf<typeof ContentAuthorLink>) => ReactNode;
  coverImage: (props?: PartialPropsOf<typeof ContentCoverImage>) => ReactNode;
  coverVideo: (props?: PartialPropsOf<typeof ContentCoverVideo>) => ReactNode;
  headline: (props?: PartialPropsOf<typeof ContentHeadline>) => ReactNode;
  liveTag: (props?: PartialPropsOf<typeof ContentLiveTag>) => ReactNode;
  printHeadline: (props?: PartialPropsOf<typeof ContentPrintHeadline>) => ReactNode;
  publishedDate: (props?: PartialPropsOf<typeof ContentPublishedDate>) => ReactNode;
  sections: (props?: PartialPropsOf<typeof ContentSections>) => ReactNode;
  sponsorTag?: (props?: PartialPropsOf<typeof SponsorTag>) => ReactNode;
  subHeadline: (props?: PartialPropsOf<typeof ContentSubHeadline>) => ReactNode;
  summary: (props?: PartialPropsOf<typeof ContentSummary>) => ReactNode;
  topicLink?: (props?: PartialPropsOf<typeof ContentTopicLink>) => ReactNode;
  video?: {
    durationOverlay: (props?: PartialPropsOf<typeof VideoDurationOverlay>) => ReactNode;
  };
};

export const ContentItemRender = forwardRef<HTMLDivElement, Props>(
  (
    {
      advertisement,
      className,
      customQueryParameters,
      reference: reference_,
      variant,
      withAuthorLink,
      withTopicLink,
      ...variantProps
    },
    reference,
  ) => {
    const content = useFragment(
      graphql`
        fragment contentItemRenderContent on Content {
          ...entityLink
          ...contentCoverImageContent
          ...contentActionBarContent
          ...contentCoverVideoContent
          ...contentPublishedDateContent
          ...contentSubHeadlineContent
          ...contentSummaryContent
          ...contentHeadlineContent
          ...contentPrintHeadlineContent
          ...contentTopicLinkContent
          ...contentLiveTagContent
          ...contentAuthorLinkContent
          ...primaryContentItemContent
          ...secondaryContentItemContent
          ...searchContentItemContent
          ...opinionContentItemContent
          ...cardContentItemContent
          ...morningStudioContentItemContent
          ...magazinesStyleLeadContentItemContent
          ...magazinesStyleCardContentItemContent
          ...magazinesStyleArticleMoreOnThisContentItemContent
          ...hooksUseContentSponsorContent
          ...postiesPrimaryContentItemContent
          ...postiesSecondaryContentItemContent
          ...postiesSectionPrimaryContentItemContent
          ...postiesSectionSecondaryContentItemContent
          ...homePrimaryContentItemContent
          ...homeSecondaryContentItemContent
          ...contentSectionsContent
          ... on Video {
            __typename
            ...videoDurationOverlayVideo
          }
          ...magazinesStyleFashionCarouselContentItemContent
          ...magazinesStyleSpotlightContentItemContent
        }
      `,
      reference_,
    );
    const contentSponsor = useContentSponsor(content);
    const provider: DefaultComponentsProvider = {
      actionBar: props => <ContentActionBar reference={content} {...props} />,
      coverImage: props => (
        <ContentCoverImage
          reference={content}
          responsiveVariants={{
            desktopUp: "size540x360",
            mobileUp: "size540x360",
            tabletUp: "size540x360",
          }}
          {...props}
        />
      ),
      coverVideo: props => <ContentCoverVideo reference={content} {...props} />,
      headline: props => (
        <ContentHeadline
          reference={content}
          useContentSponsorStyle={!!contentSponsor?.name}
          {...props}
        />
      ),
      liveTag: props => <ContentLiveTag reference={content} {...props} />,
      printHeadline: props => <ContentPrintHeadline reference={content} {...props} />,
      publishedDate: props => <ContentPublishedDate reference={content} {...props} />,
      sections: props => <ContentSections reference={content} {...props} />,
      subHeadline: props => <ContentSubHeadline reference={content} {...props} />,
      summary: props => <ContentSummary reference={content} {...props} />,
    };
    if (withAuthorLink) {
      provider.authorLink = props => <ContentAuthorLink reference={content} {...props} />;
    }
    if (withTopicLink) {
      provider.topicLink = props => (
        <ContentTopicLink {...withTopicLink} {...props} reference={content} />
      );
    }
    if (content.__typename === "Video") {
      provider.video = {
        durationOverlay: props => (
          <VideoDurationOverlay
            reference={content}
            responsiveVariants={{ desktopUp: "medium", mobileUp: "small", tabletUp: "medium" }}
            {...props}
          />
        ),
      };
    }

    if (contentSponsor?.name) {
      provider.sponsorTag = props => <SponsorTag reference={contentSponsor} {...props} />;
    }

    const ContentItemComponentMap = {
      card: ContentItemCard,
      "home-primary": ContentItemHomePrimary,
      "home-secondary": ContentItemHomeSecondary,
      "magazines-style-article-more-on-this": ContentItemMagazinesStyleArticleMoreOnThis,
      "magazines-style-card-square": ContentItemMagazinesStyleCardSquare,
      "magazines-style-card-wide": ContentItemMagazinesStyleCardWide,
      "magazines-style-fashion-carousel": ContentItemStyleFashionCarousel,
      "magazines-style-lead": ContentItemMagazinesStyleLead,
      "magazines-style-spotlight": ContentItemMagazinesStyleSpotlight,
      "morning-studio": ContentItemMorningStudio,
      opinion: ContentItemOpinion,
      "posties-primary": ContentItemPostiesPrimary,
      "posties-secondary": ContentItemPostiesSecondary,
      "posties-section-primary": ContentItemPostiesSectionPrimary,
      "posties-section-secondary": ContentItemPostiesSectionSecondary,
      primary: ContentItemPrimary,
      search: ContentItemSearch,
      secondary: ContentItemSecondary,
    };

    const ContentItemComponent = ContentItemComponentMap[variant];
    const itemProps = {
      advertisement,
      className,
      customQueryParameters,
      provider,
      ref: reference,
      reference: content,
      variantProps,
    };

    return <ContentItemComponent {...itemProps} />;
  },
);

ContentItemRender.displayName = "ContentItemRender";
